import OlMap from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import { Coordinate } from 'ol/coordinate';
import { boundingExtent } from 'ol/extent';
import { transformExtent } from 'ol/proj';
import { OrthoPhotoDto } from 'src/app/bo/orthophoto';
import {
  MAP_PROJECTION,
  MAPBOX_ATTRIBUTIONS,
  SATELLITE_EXTENT_METERS,
  WGS84_PROJECTION,
  Z_INDEX,
  mapboxSatelliteUrl,
} from '../site-map.constants';
import { SiteMapViewMode } from '../site-map.types';

const ORTHOPHOTO_VISIBLE = 'orthophotoVisible';

export class BaseLayers {
  private readonly osm = new TileLayer({ source: new OSM(), zIndex: Z_INDEX.osm });
  private readonly satellite = new TileLayer<XYZ>({ visible: false, preload: 1, zIndex: Z_INDEX.satellite });
  private orthophotoLayers: TileLayer<XYZ>[] = [];

  constructor(private readonly map: OlMap) {
    map.addLayer(this.osm);
    map.addLayer(this.satellite);
  }

  setSatelliteToken(token: string): void {
    this.satellite.setSource(new XYZ({
      url: mapboxSatelliteUrl(token),
      attributions: MAPBOX_ATTRIBUTIONS,
      tileSize: 256,
      maxZoom: 20,
    }));
  }

  /** « Plan » affiche OSM, « Map » le satellite : les deux fonds sont exclusifs. */
  setViewMode(mode: SiteMapViewMode): void {
    this.osm.setVisible(mode === 'plan2d');
    this.satellite.setVisible(mode === 'map2d');
  }

  /** Borne le fond satellite à un carré de 40 km centré sur le site, comme l'ancienne carte. */
  setSiteCenter(center: Coordinate | null): void {
    const distance = SATELLITE_EXTENT_METERS;
    this.satellite.setExtent(center
      ? boundingExtent([[center[0] - distance, center[1] - distance], [center[0] + distance, center[1] + distance]])
      : undefined);
  }

  /** Remplace les orthophotos du site : le plus petit zOrder est rendu au-dessus des autres. */
  setOrthophotos(orthophotos: OrthoPhotoDto[], visible: boolean): void {
    this.removeOrthophotos();
    const displayable = orthophotos.filter(orthophoto => !!orthophoto.tiles && !!orthophoto.bounds);
    if (!displayable.length) return;

    const maxZOrder = Math.max(...displayable.map(orthophoto => orthophoto.zOrder));
    this.orthophotoLayers = displayable.map(orthophoto => {
      const layer = new TileLayer<XYZ>({
        extent: transformExtent(orthophoto.bounds, WGS84_PROJECTION, MAP_PROJECTION),
        source: new XYZ({ url: orthophoto.tiles }),
        visible: visible && orthophoto.isVisible,
        zIndex: Z_INDEX.orthophotos + (maxZOrder - orthophoto.zOrder),
      });
      layer.set(ORTHOPHOTO_VISIBLE, orthophoto.isVisible);
      this.map.addLayer(layer);
      return layer;
    });
  }

  setOrthophotosVisible(visible: boolean): void {
    this.orthophotoLayers.forEach(layer => layer.setVisible(visible && layer.get(ORTHOPHOTO_VISIBLE)));
  }

  dispose(): void {
    this.removeOrthophotos();
    this.map.removeLayer(this.osm);
    this.map.removeLayer(this.satellite);
  }

  private removeOrthophotos(): void {
    this.orthophotoLayers.forEach(layer => this.map.removeLayer(layer));
    this.orthophotoLayers = [];
  }
}
